import { AsyncPipe, NgFor, NgIf } from "@angular/common";
import { Component, OnDestroy, OnInit, ViewEncapsulation } from "@angular/core";
import { FormControl, ReactiveFormsModule } from "@angular/forms";
import { MatAutocompleteModule } from "@angular/material/autocomplete";
import { MatButtonModule } from "@angular/material/button";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { MatTooltipModule } from "@angular/material/tooltip";
import { ActivatedRoute, Router, RouterLink, RouterOutlet } from "@angular/router";
import { ContactUsButtonComponent } from "@fuse/components/contact-us-button/contact-us-button.component";
import { FuseFullscreenComponent } from "@fuse/components/fullscreen";
import { FuseLoadingBarComponent } from "@fuse/components/loading-bar";
import { FuseNavigationService, FuseVerticalNavigationComponent } from "@fuse/components/navigation";
import { FuseConfigService } from "@fuse/services/config";
import { FuseMediaWatcherService } from "@fuse/services/media-watcher";
import { TranslocoPipe } from "@ngneat/transloco";
import { OrganizationBrandingService } from "app/core/branding/branding.service";
import { NavigationService } from "app/core/navigation/navigation.service";
import { Navigation } from "app/core/navigation/navigation.types";
import { LanguagesComponent } from "app/layout/common/languages/languages.component";
import { UserComponent } from "app/layout/common/user/user.component";
import { AdvertisersService } from "app/modules/admin/advertisers/advertisers.service";
import { Advertiser } from "app/modules/admin/advertisers/interfaces/advertiser.interface";
import { SelectedAdvertiserService } from "app/services/selected-advertiser.service";
import { AcButtonComponent } from "app/shared/components/ac-button/ac-button.component";
import { OrganizationSelectorComponent } from "app/layout/common/organization-selector/organization-selector.component";
import { UploadIndicatorComponent } from "app/layout/common/upload-indicator/upload-indicator.component";
import { ChangelogButtonComponent } from "app/layout/common/changelog-button/changelog-button.component";
import { ClientViewStripComponent } from "app/layout/common/client-view-strip/client-view-strip.component";
import { NotificationBannerComponent } from "app/layout/common/notification-banner/notification-banner.component";
import { environment } from "environments/environment";
import { map, Observable, startWith, Subject, takeUntil } from "rxjs";
import { StorageService } from "../../../../services/storage.service";

@Component({
    selector: "dense-layout",
    templateUrl: "./dense.component.html",
    styleUrls: ["./dense.component.scss"],
    encapsulation: ViewEncapsulation.None,
    standalone: true,
    imports: [
        FuseLoadingBarComponent,
        FuseVerticalNavigationComponent,
        MatButtonModule,
        MatIconModule,
        MatAutocompleteModule,
        MatFormFieldModule,
        MatInputModule,
        ReactiveFormsModule,
        LanguagesComponent,
        FuseFullscreenComponent,
        UserComponent,
        NgIf,
        NgFor,
        AsyncPipe,
        RouterOutlet,
        TranslocoPipe,
        RouterLink,
        ContactUsButtonComponent,
        AcButtonComponent,
        MatTooltipModule,
        UploadIndicatorComponent,
        OrganizationSelectorComponent,
        ChangelogButtonComponent,
        NotificationBannerComponent,
        ClientViewStripComponent,
    ],
})
export class DenseLayoutComponent implements OnInit, OnDestroy {
    isScreenSmall!: boolean;
    navigation!: Navigation;
    navigationAppearance: "default" | "dense" = "dense";
    appName: string = "AppCelerate Freemium";
    logoUrl: string = "assets/images/logo/GeoActivate-compact-color.svg";
    logoMaxWidth: number | null = null;

    // Autocomplete properties
    searchControl = new FormControl<string | Advertiser>("");
    advertisers: Advertiser[] = [];
    filteredOptions!: Observable<Advertiser[]>;
    isPreProductionEnvironment: boolean = environment.environment === "preproduction";
    isDevelopmentEnvironment: boolean = environment.environment === "development";

    private _unsubscribeAll: Subject<any> = new Subject<any>();

    /**
     * Constructor
     */
    constructor(
        private _activatedRoute: ActivatedRoute,
        private _router: Router,
        private _navigationService: NavigationService,
        private _fuseMediaWatcherService: FuseMediaWatcherService,
        private _fuseNavigationService: FuseNavigationService,
        private _configService: FuseConfigService,
        private _localStorageService: StorageService,
        private _advertisersService: AdvertisersService,
        private _selectedAdvertiserService: SelectedAdvertiserService,
        private _brandingService: OrganizationBrandingService
    ) {
        this._configService.config$.pipe(takeUntil(this._unsubscribeAll)).subscribe((conf) => {
            this.appName = conf.app.name ?? this.appName;
        });
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for current year
     */
    get currentYear(): number {
        return new Date().getFullYear();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */

    ngOnInit(): void {
        // Logo from branding$ (API + local config applied in branding service)
        this._brandingService.branding$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((branding) => {
                if (branding?.logoUrl) {
                    this.logoUrl = branding.logoUrl;
                    this.logoMaxWidth = branding.logoMaxWidth ?? null;
                }
            });

        // Initialize filtered options first
        this.filteredOptions = this.searchControl.valueChanges.pipe(
            startWith(""),
            map((value) => {
                // If value is an object (Advertiser), get its name for filtering
                const searchValue = typeof value === "string" ? value : (value as Advertiser)?.name || "";
                return this._filter(searchValue);
            })
        );

        // Load advertiser from localStorage if exists
        const savedAdvertiser = this._selectedAdvertiserService.getSelectedAdvertiser();
        if (savedAdvertiser) {
            this.searchControl.setValue(savedAdvertiser);
        }

        // Load advertisers from service
        this._advertisersService
            .list()
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((advertisers: Advertiser[]) => {
                this.advertisers = advertisers;
            });

        this._navigationService.navigation$.pipe(takeUntil(this._unsubscribeAll)).subscribe((navigation: Navigation) => {
            this.navigation = navigation;
        });

        this._fuseMediaWatcherService.onMediaChange$.pipe(takeUntil(this._unsubscribeAll)).subscribe(({ matchingAliases }) => {
            this._updateNavigationAppearance(matchingAliases);
        });

        // Subscribe to router events to detect navigation appearance changes
        this._router.events.pipe(takeUntil(this._unsubscribeAll)).subscribe(() => {
            // Check localStorage for appearance changes
            const storedAppearance = this._localStorageService.getItem("navigationAppearance") as "default" | "dense";
            if (storedAppearance && storedAppearance !== this.navigationAppearance) {
                this.navigationAppearance = storedAppearance;
            }
        });
    }

    /**
     * Update navigation appearance based on screen size
     */
    private _updateNavigationAppearance(matchingAliases: string[]): void {
        // Desktop (md y arriba): grande
        if (matchingAliases.includes("md") && this._localStorageService.getItem("navigationAppearance") !== "dense") {
            this.navigationAppearance = "default";
        }
        // Tablet (sm): pequeño
        else if (matchingAliases.includes("sm")) {
            this.navigationAppearance = "dense";
        }
        // Móvil (xs): siempre pequeño
        else {
            this.navigationAppearance = "dense";
        }
    }
    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Toggle navigation
     *
     * @param name
     */
    toggleNavigation(name: string): void {
        // Get the navigation
        const navigation = this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(name);

        if (navigation) {
            // Toggle the opened status
            navigation.toggle();
        }
    }

    /**
     * Toggle the navigation appearance
     */
    toggleNavigationAppearance(): void {
        this.navigationAppearance = this.navigationAppearance === "default" ? "dense" : "default";
        this._localStorageService.setItem("navigationAppearance", this.navigationAppearance);
    }
    /**
     * Filter autocomplete options
     */
    private _filter(value: string): Advertiser[] {
        const filterValue = value.toLowerCase();

        // If no filter value, return all advertisers
        if (!filterValue.trim()) {
            return this.advertisers;
        }

        // Filter advertisers that match the search value
        return this.advertisers.filter((advertiser) => advertiser.name.toLowerCase().includes(filterValue));
    }

    /**
     * Handle autocomplete option selection
     */
    onOptionSelected(advertiser: Advertiser): void {
        // Set the selected advertiser as the form control value
        this.searchControl.setValue(advertiser);
        // Emit the selected advertiser to other components
        this._selectedAdvertiserService.setSelectedAdvertiser(advertiser);
    }

    /**
     * Display function for autocomplete
     */
    displayAdvertiser(advertiser: Advertiser | string): string {
        if (typeof advertiser === "string") {
            return advertiser;
        }
        return advertiser?.name || "";
    }

    /**
     * Handle input click to show all options
     */
    onInputClick(): void {
        // Only clear if the current value is a string (not a selected Advertiser object)
        const currentValue = this.searchControl.value;
        if (typeof currentValue === "string") {
            this.searchControl.setValue("");
        }
        // Trigger a manual update to ensure all options are shown
        this.searchControl.updateValueAndValidity();
    }

    /**
     * Clear advertiser selection
     */
    clearAdvertiserSelection(): void {
        this.searchControl.setValue("");
        this._selectedAdvertiserService.setSelectedAdvertiser(null);
    }
}
